import React, { lazy, Suspense, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { func, object } from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { Button } from '@fiverr-private/fit';
import { getContext, I18n } from '@fiverr-private/fiverr_context';
import { EducationTooltip } from '@fiverr-private/orca';
import pathfinder from '@fiverr-private/pathfinder';
import { CollectionActionType } from '../../../../../shared/types/collectionActionType';
import eventsReporter from '../../../../../shared/utils/events/reporter';
import { EventTypes } from '../../../../../shared/utils/events/types';
import { shareWithTeam } from '../../../store/collection/actions';
import { redirectTo } from '../../../../../shared/utils/redirect';
import SharedContext from '../../../../../shared/context';
import { EducationTooltipStorageType } from '../../../../../shared/types/educationTooltipStorageType';
import { MEDIA_GRID_SM } from '../../../../../shared/constants';

const ShareWithTeamModal = lazy(() =>
    import(/* webpackChunkName: 'ShareWithTeamModal' */ '../../../../../shared/components/ShareWithTeamModal')
);

const { header } = EventTypes.collectionPage;

const ShareWithTeamBtn = ({ collection, shareWithTeam }) => {
    const [loading, setLoading] = useState(false);
    const [shouldRenderModal, setShouldRenderModal] = useState(false);
    const { creator } = useContext(SharedContext);

    const onShareWithTeamClick = () => {
        const actionName = CollectionActionType.SHARE_WITH_TEAM;
        eventsReporter.report(header.actions[actionName].clicked, {
            actionName,
        });

        setLoading(true);
        setShouldRenderModal(true);
    };

    const onSuccessfulSubmit = ({ slug }) => {
        const { pathParameters = {} } = getContext();

        const { id: slugParam } = pathParameters;

        if (slug !== slugParam) {
            redirectTo(
                pathfinder('collection_page_lists', {
                    user_id: creator.username,
                    id: slug,
                })
            );
        }
    };

    const isMobile = useMediaQuery({ maxWidth: MEDIA_GRID_SM });
    const position = isMobile ? EducationTooltip.POSITIONS.TOP_LEFT : EducationTooltip.POSITIONS.TOP;

    return (
        <>
            <EducationTooltip
                title={<I18n k="collections_perseus.collection_page.education.share_with_team.title" />}
                position={position}
                className="share-with-team-btn-education"
                body={<I18n k="collections_perseus.collection_page.education.share_with_team.body" />}
                tooltipStorageKey={EducationTooltipStorageType.SHARE_WITH_TEAM}
                badgeTitle={<I18n k="collections_perseus.tooltips.new" />}
                closeTitle={<I18n k="collections_perseus.tooltips.got_it" />}
            >
                <Button
                    fill={Button.FILLS.GHOST}
                    color={Button.COLORS.GREY}
                    loading={loading}
                    onClick={onShareWithTeamClick}
                    className="share-with-team-btn"
                >
                    <I18n k="collections_perseus.collection_page.share_with_team_cta" />
                </Button>
            </EducationTooltip>
            {shouldRenderModal && (
                <Suspense fallback={null}>
                    <ShareWithTeamModal
                        collection={collection}
                        onClose={() => setShouldRenderModal(false)}
                        onSubmit={shareWithTeam}
                        onSuccessfulSubmit={onSuccessfulSubmit}
                        onLoad={() => setLoading(false)}
                    />
                </Suspense>
            )}
        </>
    );
};

ShareWithTeamBtn.propTypes = {
    collection: object,
    shareWithTeam: func,
};

const mapStateToProps = ({ collection }) => ({ collection });

const mapDispatchToProps = { shareWithTeam };

export { ShareWithTeamBtn };
export default connect(mapStateToProps, mapDispatchToProps)(ShareWithTeamBtn);
